import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickUnsyncTarget } from "@/features/pathpick/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 상품별 처리 내역 / 신청 상품 내역 양쪽에서 열리므로 공용 대상 타입을 받는다.
  target: PathPickUnsyncTarget;
  isLoading: boolean;
  onUnsyncOnly: () => void;
  onDelete: () => void;
}

// 연동 상품 관리 — 연동만 해제 / 상품 삭제 선택 모달.
// 실제 mutation은 부모(각 목록 모달)가 소유한다.
function PathPickManageProductModal({
  isOpen,
  onClose,
  target,
  isLoading,
  onUnsyncOnly,
  onDelete,
}: Props) {
  const { t } = useI18nHelper();

  const noInfoLabel = t(
    "path_pick:sync_download_history.product_requests_modal.no_info"
  );

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"min(520px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:sync_download_history.manage_modal.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Flex
            flexDirection={"column"}
            gap={"2px"}
            padding={"12px 16px"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
            background={"#F7F9FA"}
          >
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {`${target.brandName ?? noInfoLabel} · ${
                target.name ?? noInfoLabel
              }`}
            </Text>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {target.sku ?? noInfoLabel}
            </Text>
          </Flex>

          <Flex
            flexDirection={"column"}
            gap={"4px"}
            padding={"12px 16px"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
          >
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {t(
                "path_pick:sync_download_history.manage_modal.unsync_only_title"
              )}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {t(
                "path_pick:sync_download_history.manage_modal.unsync_only_description"
              )}
            </Text>
          </Flex>

          <Flex
            flexDirection={"column"}
            gap={"4px"}
            padding={"12px 16px"}
            border={"0.5px solid #EE7D7A"}
            borderRadius={"8px"}
            background={"#FFEEEE"}
          >
            <Text textStyle={"body-md-bold"} color={"#EE7D7A"}>
              {t("path_pick:sync_download_history.manage_modal.delete_title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {t(
                "path_pick:sync_download_history.manage_modal.delete_description"
              )}
            </Text>
          </Flex>

          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t("path_pick:sync_download_history.manage_modal.caption")}
          </Text>

          {/* 액션이 3개(취소/연동만 해제/상품 삭제)라 공용 Footer 대신 자체 푸터로 처리 */}
          <Flex justifyContent={"flex-end"} gap={"8px"}>
            <AppButton variant={"white"} onClick={onClose}>
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:sync_download_history.manage_modal.cancel")}
              </Text>
            </AppButton>
            <AppButton
              variant={"white"}
              isLoading={isLoading}
              onClick={onUnsyncOnly}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"x-circle"}
                  width={16}
                  height={16}
                  color={"#444440"}
                />
                <Text textStyle={"body-sm-bold"}>
                  {t(
                    "path_pick:sync_download_history.manage_modal.unsync_only"
                  )}
                </Text>
              </Flex>
            </AppButton>
            <AppButton
              variant={"white"}
              isLoading={isLoading}
              onClick={onDelete}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"trash"}
                  width={16}
                  height={16}
                  color={"#EE7D7A"}
                />
                <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
                  {t("path_pick:sync_download_history.manage_modal.delete")}
                </Text>
              </Flex>
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickManageProductModal;
